import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { State } from '../shared/model';
import {
  manualMetricDefinitions,
  type ManualMetricReading,
  type ManualMetricStatus,
  type ManualMetricsComparison,
  type ManualMetricsWorkspaceData,
  type ManualWindowKind,
} from '../shared/manual-metrics';
import './manual-metrics.css';

const kinds: Record<ManualWindowKind, string> = {
  lifetime_snapshot: 'Acumulado observado',
  native_window: 'Janela nativa informada pela fonte',
  estimate: 'Estimativa com método declarado',
};
const statuses: Record<ManualMetricStatus, string> = {
  available: 'Disponível',
  not_reported: 'Não informado',
  no_permission: 'Sem permissão',
  not_applicable: 'Não aplicável',
  error: 'Erro de leitura',
};
const field = (data: FormData, key: string) => String(data.get(key) ?? '').trim();
const stamp = (value: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T12:00:00Z`).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
    : new Date(value).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
const localInput = (value: string, timezone = 'America/Sao_Paulo') => {
  if (!value || !Number.isFinite(Date.parse(value))) return '';
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(value));
  const get = (key: string) => parts.find((part) => part.type === key)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`;
};
const toInstant = (value: string, timezone = 'America/Sao_Paulo') => {
  const target = Date.parse(`${value}Z`);
  if (!Number.isFinite(target)) throw new Error('Informe o instante da observação.');
  let instant = target;
  for (let pass = 0; pass < 3; pass++)
    instant += target - Date.parse(`${localInput(new Date(instant).toISOString(), timezone)}Z`);
  return new Date(instant).toISOString();
};
function closureLabel(reading: ManualMetricReading) {
  switch (reading.closure.classification) {
    case 'native_d7':
      return 'Janela nativa informada: sete dias desde a publicação';
    case 'snapshot_at_d7':
      return 'Acumulado observado no instante D+7';
    case 'snapshot_after_d7':
      return `Acumulado após D+7 · atraso de ${((reading.closure.delaySeconds ?? 0) / 3600).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} h`;
    case 'day_precision_pending':
      return 'Publicação com precisão de dia; fechamento D+7 exato indisponível';
    case 'estimate':
      return 'Estimativa; não classificada como fechamento D+7';
    default:
      return 'Sem classificação de fechamento D+7';
  }
}
type Editor = { publicationId: string; requestId: string; reading?: ManualMetricReading };
type Props = { campaignId: string; data: State; refresh?: () => Promise<unknown> };
export function ManualMetricsWorkspace({ campaignId, data, refresh }: Props) {
  const [workspace, setWorkspace] = useState<ManualMetricsWorkspaceData>();
  const [editor, setEditor] = useState<Editor>();
  const [kind, setKind] = useState<ManualWindowKind>('lifetime_snapshot');
  const [precision, setPrecision] = useState<'day' | 'second'>('second');
  const [timezone, setTimezone] = useState<'America/Sao_Paulo' | 'UTC'>('America/Sao_Paulo');
  const [comparison, setComparison] = useState<ManualMetricsComparison>();
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const generation = useRef(0);
  const endpoint = `/api/campaigns/${encodeURIComponent(campaignId)}/manual-metrics`;
  async function load() {
    const token = ++generation.current;
    const response = await fetch(endpoint),
      result = await response.json();
    if (!response.ok)
      throw new Error(result.error || 'Não foi possível consultar as leituras manuais.');
    if (token === generation.current) setWorkspace(result);
  }
  useEffect(() => {
    setWorkspace(undefined);
    setEditor(undefined);
    setComparison(undefined);
    setError('');
    setNotice('');
    void load().catch((reason) => setError(reason.message));
    return () => {
      generation.current++;
    };
  }, [campaignId]);
  const title = (contentId: string) => {
    const content = data.contents.find((item) => item.id === contentId);
    return content
      ? `${content.flgId ? `${content.flgId} · ` : ''}${content.title}`
      : 'Publicação registrada';
  };
  const selected = workspace?.publications.find((item) => item.id === editor?.publicationId);
  function open(publicationId: string, reading?: ManualMetricReading) {
    setError('');
    setNotice('');
    setEditor({ publicationId, reading, requestId: crypto.randomUUID() });
    setKind(reading?.kind ?? 'lifetime_snapshot');
    setPrecision(reading?.windowPrecision ?? 'second');
    setTimezone(reading?.timezone ?? 'America/Sao_Paulo');
  }
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editor || !selected || busy) return;
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const metrics = manualMetricDefinitions.map(({ key }) => {
        const status = field(form, `${key}:status`) as ManualMetricStatus;
        const raw = field(form, `${key}:value`);
        let value: number | null = null;
        if (status === 'available') {
          if (!/^\d+$/.test(raw) || !Number.isSafeInteger(Number(raw)))
            throw new Error(
              'Métrica disponível exige uma contagem inteira; use zero somente se a fonte informou zero.',
            );
          value = Number(raw);
        }
        return { key, status, value, unit: 'count', note: field(form, `${key}:note`) };
      });
      const body = {
        publicationId: selected.id,
        requestId: editor.requestId,
        replacesId: editor.reading?.id ?? null,
        correctionReason: field(form, 'correctionReason'),
        source: field(form, 'source'),
        evidence: field(form, 'evidence'),
        organicEvidence: field(form, 'organicEvidence'),
        organicConfirmed: form.has('organicConfirmed'),
        sourceVersion: field(form, 'sourceVersion'),
        collectedAt: toInstant(field(form, 'collectedAt')),
        kind,
        windowStart:
          kind === 'lifetime_snapshot'
            ? ''
            : precision === 'day'
              ? field(form, 'windowStart')
              : toInstant(field(form, 'windowStart'), timezone),
        windowEnd:
          kind === 'lifetime_snapshot'
            ? ''
            : precision === 'day'
              ? field(form, 'windowEnd')
              : toInstant(field(form, 'windowEnd'), timezone),
        windowPrecision: precision,
        timezone,
        coverage: field(form, 'coverage'),
        coverageNote: field(form, 'coverageNote'),
        method: field(form, 'method'),
        metrics,
      };
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-FLG-CSRF': data.csrf },
        body: JSON.stringify(body),
      });
      const result = await response.json();
      if (!response.ok) {
        if (response.status === 409) await load();
        throw new Error(result.error || 'Não foi possível salvar.');
      }
      setEditor(undefined);
      setComparison(undefined);
      await load();
      if (refresh) await refresh();
      setNotice('Leitura manual registrada. Histórico e fontes preservados.');
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function compare(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    const form = new FormData(event.currentTarget);
    try {
      const query = new URLSearchParams({ left: field(form, 'left'), right: field(form, 'right') });
      const response = await fetch(`${endpoint}/compare?${query}`),
        result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Não foi possível comparar.');
      setComparison(result);
    } catch (reason) {
      setError((reason as Error).message);
    }
  }
  const currentIds = new Set(workspace?.currentReadings.map((reading) => reading.id));
  return (
    <section className="mm-workspace" aria-labelledby="mm-heading">
      <div className="mm-heading">
        <div>
          <h2 id="mm-heading">Métricas orgânicas manuais</h2>
          <p>
            Registre valores que a fonte identifica como orgânicos. Leituras totais da Meta e
            resultados pagos continuam separados.
          </p>
        </div>
        <a
          className="button"
          href={`/api/campaigns/${encodeURIComponent(campaignId)}/manual-metrics.csv`}
        >
          Exportar leituras manuais
        </a>
      </div>
      {error && (
        <p role="alert" className="notice error">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="notice">
          {notice}
        </p>
      )}
      {!workspace ? (
        <p>Carregando leituras…</p>
      ) : !workspace.publications.length ? (
        <p className="mm-note">
          Registre uma publicação real da campanha para incluir suas métricas. A etapa editorial
          sozinha não comprova publicação.
        </p>
      ) : (
        <>
          <p className="mm-note">
            Zero precisa estar confirmado. Sem dado, sem permissão e erro são estados próprios. As
            leituras acumuladas não são somadas, e alcance permanece por publicação.
          </p>
          {editor && selected && (
            <form
              className="mm-form"
              key={`${editor.publicationId}:${editor.requestId}`}
              onSubmit={submit}
            >
              <h3>
                {editor.reading
                  ? 'Corrigir leitura preservando o original'
                  : 'Registrar leitura orgânica'}{' '}
                · {title(selected.contentId)}
              </h3>
              <p>
                {selected.platform} · {selected.account} · publicação em{' '}
                {stamp(selected.publishedAt)}
              </p>
              <fieldset disabled={busy}>
                <label>
                  Fonte
                  <input
                    name="source"
                    required
                    minLength={3}
                    maxLength={250}
                    defaultValue={editor.reading?.source}
                    placeholder="Ex.: relatório orgânico do painel da plataforma"
                  />
                </label>
                <label>
                  Versão ou referência da fonte
                  <input
                    name="sourceVersion"
                    maxLength={250}
                    defaultValue={editor.reading?.sourceVersion}
                  />
                </label>
                <label>
                  Instante observado · São Paulo
                  <input
                    name="collectedAt"
                    type="datetime-local"
                    step="1"
                    required
                    defaultValue={editor.reading ? localInput(editor.reading.collectedAt) : ''}
                  />
                </label>
                <label>
                  Tipo de leitura
                  <select
                    value={kind}
                    onChange={(event) => setKind(event.target.value as ManualWindowKind)}
                  >
                    {Object.entries(kinds).map(([value, label]) => (
                      <option value={value} key={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Fuso da janela
                  <select
                    value={timezone}
                    onChange={(event) => setTimezone(event.target.value as typeof timezone)}
                  >
                    <option value="America/Sao_Paulo">São Paulo</option>
                    <option value="UTC">UTC</option>
                  </select>
                </label>
                {kind !== 'lifetime_snapshot' && (
                  <>
                    <label>
                      Precisão dos limites
                      <select
                        value={precision}
                        onChange={(event) => setPrecision(event.target.value as typeof precision)}
                      >
                        <option value="second">Instantes conhecidos</option>
                        <option value="day">Dias de calendário</option>
                      </select>
                    </label>
                    <label>
                      Início da janela
                      <input
                        key={`start:${precision}:${timezone}`}
                        name="windowStart"
                        type={precision === 'day' ? 'date' : 'datetime-local'}
                        step="1"
                        required
                        defaultValue={
                          editor.reading && editor.reading.kind !== 'lifetime_snapshot'
                            ? precision === 'day'
                              ? editor.reading.windowStart.slice(0, 10)
                              : localInput(editor.reading.windowStart, timezone)
                            : ''
                        }
                      />
                    </label>
                    <label>
                      Fim da janela{' '}
                      {precision === 'day' ? '(dia incluído)' : '(instante exclusivo)'}
                      <input
                        key={`end:${precision}:${timezone}`}
                        name="windowEnd"
                        type={precision === 'day' ? 'date' : 'datetime-local'}
                        step="1"
                        required
                        defaultValue={
                          editor.reading && editor.reading.kind !== 'lifetime_snapshot'
                            ? precision === 'day'
                              ? editor.reading.windowEnd.slice(0, 10)
                              : localInput(editor.reading.windowEnd, timezone)
                            : ''
                        }
                      />
                    </label>
                    <p className="mm-wide mm-note">
                      Informe os limites mostrados pela fonte. Janelas por dia precisam estar
                      concluídas antes do dia da coleta. D+7 só é classificado quando a publicação e
                      os limites têm precisão suficiente.
                    </p>
                  </>
                )}
                {kind === 'lifetime_snapshot' && (
                  <p className="mm-wide mm-note">
                    O acumulado vai da publicação real até o instante observado. Isso conserva a
                    precisão original da publicação e não cria uma janela nativa de sete dias.
                  </p>
                )}
                <label>
                  Cobertura
                  <select name="coverage" defaultValue={editor.reading?.coverage ?? 'complete'}>
                    <option value="complete">Publicação inteira, conforme a fonte</option>
                    <option value="partial">Parcial ou limitada</option>
                  </select>
                </label>
                <label>
                  Limites de cobertura
                  <input
                    name="coverageNote"
                    maxLength={12000}
                    defaultValue={editor.reading?.coverageNote}
                    placeholder="Obrigatório quando a cobertura é parcial"
                  />
                </label>
                <label className="mm-wide">
                  Método da leitura ou estimativa
                  <textarea
                    name="method"
                    required
                    minLength={3}
                    rows={2}
                    defaultValue={editor.reading?.method}
                    placeholder="Onde o número foi obtido; em estimativas, cálculo e limitações"
                  />
                </label>
                <label className="mm-wide">
                  Evidência da leitura
                  <textarea
                    name="evidence"
                    required
                    minLength={3}
                    rows={3}
                    defaultValue={editor.reading?.evidence}
                    placeholder="Referência ao relatório, captura e o que foi conferido"
                  />
                </label>
                <label className="mm-wide">
                  Como a fonte separa o orgânico
                  <textarea
                    name="organicEvidence"
                    required
                    minLength={3}
                    rows={2}
                    defaultValue={editor.reading?.organicEvidence}
                    placeholder="Identifique o filtro ou campo orgânico mostrado pela fonte"
                  />
                </label>
                <label className="mm-wide mm-check">
                  <input
                    type="checkbox"
                    name="organicConfirmed"
                    required
                    defaultChecked={Boolean(editor.reading)}
                  />
                  Conferi que estes números estão identificados como orgânicos na fonte.
                </label>
                <div className="mm-wide mm-metric-inputs">
                  {manualMetricDefinitions.map((metric) => (
                    <MetricInput key={metric.key} definition={metric} reading={editor.reading} />
                  ))}
                </div>
                {editor.reading && (
                  <label className="mm-wide">
                    Motivo da correção
                    <textarea name="correctionReason" required minLength={3} rows={2} />
                  </label>
                )}
                <div className="mm-wide mm-actions">
                  <button type="button" onClick={() => setEditor(undefined)}>
                    Cancelar leitura
                  </button>
                  <button className="primary" type="submit">
                    {busy
                      ? 'Salvando…'
                      : editor.reading
                        ? 'Salvar correção'
                        : 'Salvar leitura manual'}
                  </button>
                </div>
              </fieldset>
            </form>
          )}
          <div className="mm-publications">
            {workspace.publications.map((publication) => {
              const history = workspace.readings.filter((reading) =>
                publication.readingIds.includes(reading.id),
              );
              return (
                <article className="mm-publication" key={publication.id}>
                  <div className="mm-heading">
                    <div>
                      <h3>{title(publication.contentId)}</h3>
                      <p>
                        {publication.platform} · {publication.account} ·{' '}
                        {stamp(publication.publishedAt)}
                        {publication.precision === 'day'
                          ? ' · horário não informado'
                          : ' · São Paulo'}
                      </p>
                    </div>
                    {data.permissions.edit && (
                      <button disabled={busy} onClick={() => open(publication.id)}>
                        Registrar leitura
                      </button>
                    )}
                  </div>
                  {!history.length ? (
                    <p>Nenhuma leitura orgânica manual registrada.</p>
                  ) : (
                    <details open>
                      <summary>
                        {history.length}{' '}
                        {history.length === 1 ? 'leitura registrada' : 'leituras registradas'} ·
                        histórico completo
                      </summary>
                      {history.map((reading) => (
                        <section
                          className={`mm-reading ${currentIds.has(reading.id) ? '' : 'mm-replaced'}`}
                          key={reading.id}
                        >
                          <div className="mm-heading">
                            <div>
                              <strong>
                                {kinds[reading.kind]} · {stamp(reading.collectedAt)}
                              </strong>
                              <p>
                                Revisão {reading.revision} ·{' '}
                                {currentIds.has(reading.id)
                                  ? 'Vigente'
                                  : 'Corrigida, preservada no histórico'}{' '}
                                ·{' '}
                                {reading.coverage === 'complete'
                                  ? 'Cobertura da publicação inteira'
                                  : 'Cobertura parcial'}
                              </p>
                            </div>
                            {data.permissions.edit && currentIds.has(reading.id) && (
                              <button disabled={busy} onClick={() => open(publication.id, reading)}>
                                Corrigir leitura
                              </button>
                            )}
                          </div>
                          <p className="mm-note">{closureLabel(reading)}</p>
                          <p>
                            Janela: {reading.windowStart} → {reading.windowEnd} · {reading.timezone}{' '}
                            · precisão{' '}
                            {reading.windowPrecision === 'day' ? 'de dia' : 'de instante'}
                            {reading.endInclusive ? ' · dia final incluído' : ''}
                          </p>
                          <div className="mm-table">
                            <table>
                              <thead>
                                <tr>
                                  <th>Métrica</th>
                                  <th>Estado</th>
                                  <th>Valor</th>
                                </tr>
                              </thead>
                              <tbody>
                                {reading.metrics.map((metric) => (
                                  <tr key={metric.key}>
                                    <td>
                                      {
                                        manualMetricDefinitions.find(
                                          (item) => item.key === metric.key,
                                        )?.label
                                      }
                                    </td>
                                    <td>{statuses[metric.status]}</td>
                                    <td>
                                      {metric.status === 'available'
                                        ? metric.value?.toLocaleString('pt-BR')
                                        : '—'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <details>
                            <summary>Fonte, evidência e método</summary>
                            <dl>
                              <dt>Fonte</dt>
                              <dd>
                                {reading.source}
                                {reading.sourceVersion ? ` · ${reading.sourceVersion}` : ''}
                              </dd>
                              <dt>Evidência</dt>
                              <dd>{reading.evidence}</dd>
                              <dt>Separação orgânica</dt>
                              <dd>{reading.organicEvidence}</dd>
                              <dt>Método</dt>
                              <dd>{reading.method}</dd>
                              <dt>Cobertura</dt>
                              <dd>
                                {reading.coverageNote || 'Publicação inteira conforme a fonte'}
                              </dd>
                              {reading.correctionReason && (
                                <>
                                  <dt>Correção</dt>
                                  <dd>{reading.correctionReason}</dd>
                                </>
                              )}
                              <dt>Registro</dt>
                              <dd>
                                {reading.recordedBy} · {stamp(reading.createdAt)}
                              </dd>
                            </dl>
                          </details>
                        </section>
                      ))}
                    </details>
                  )}
                </article>
              );
            })}
          </div>
          {workspace.currentReadings.length > 1 && (
            <section className="mm-comparison">
              <h3>Comparar leituras compatíveis</h3>
              <form onSubmit={compare}>
                <label>
                  Leitura inicial
                  <select name="left" required>
                    {workspace.currentReadings.map((reading) => (
                      <option key={reading.id} value={reading.id}>
                        {title(reading.contentId)} · {reading.account} ·{' '}
                        {stamp(reading.collectedAt)} · {kinds[reading.kind]}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Leitura final
                  <select name="right" required defaultValue={workspace.currentReadings[0].id}>
                    {workspace.currentReadings.map((reading) => (
                      <option key={reading.id} value={reading.id}>
                        {title(reading.contentId)} · {reading.account} ·{' '}
                        {stamp(reading.collectedAt)} · {kinds[reading.kind]}
                      </option>
                    ))}
                  </select>
                </label>
                <button type="submit">Comparar leituras</button>
              </form>
              {comparison && (
                <div role="status">
                  <p>
                    {comparison.compatible
                      ? comparison.note
                      : 'Comparação automática indisponível.'}
                  </p>
                  {comparison.reasons.map((reason) => (
                    <p key={reason}>{reason}</p>
                  ))}
                  {comparison.compatible && (
                    <div className="mm-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Métrica</th>
                            <th>Inicial</th>
                            <th>Final</th>
                            <th>Diferença</th>
                            <th>Variação</th>
                          </tr>
                        </thead>
                        <tbody>
                          {comparison.metrics
                            .filter(
                              (metric) => metric.leftValue !== null || metric.rightValue !== null,
                            )
                            .map((metric) => (
                              <tr key={metric.key}>
                                <td>
                                  {
                                    manualMetricDefinitions.find((item) => item.key === metric.key)
                                      ?.label
                                  }
                                </td>
                                <td>{metric.leftValue ?? '—'}</td>
                                <td>{metric.rightValue ?? '—'}</td>
                                <td>{metric.difference ?? '—'}</td>
                                <td>
                                  {metric.percentageChange === null
                                    ? 'Não calculada'
                                    : `${metric.percentageChange.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}
        </>
      )}
    </section>
  );
}
function MetricInput({
  definition,
  reading,
}: {
  definition: (typeof manualMetricDefinitions)[number];
  reading?: ManualMetricReading;
}) {
  const metric = reading?.metrics.find((metric) => metric.key === definition.key);
  const [status, setStatus] = useState<ManualMetricStatus>(metric?.status ?? 'not_reported');
  return (
    <div className="mm-metric-input">
      <strong>{definition.label}</strong>
      <label>
        Estado de {definition.label}
        <select
          name={`${definition.key}:status`}
          value={status}
          onChange={(event) => setStatus(event.target.value as ManualMetricStatus)}
        >
          {Object.entries(statuses).map(([value, label]) => (
            <option value={value} key={value}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <label>
        Valor de {definition.label}
        <input
          name={`${definition.key}:value`}
          type="number"
          min="0"
          max={Number.MAX_SAFE_INTEGER}
          step="1"
          required={status === 'available'}
          disabled={status !== 'available'}
          defaultValue={metric?.value ?? ''}
        />
      </label>
      <label>
        Observação de {definition.label}
        <input name={`${definition.key}:note`} maxLength={250} defaultValue={metric?.note} />
      </label>
    </div>
  );
}
